import { grpcClientFactory } from './grpcClientFactory';
import { accountPlanFromProto } from '../utils/entitlements';
const PLAN_SKU_TO_PROTO = {
    single_map: 1,
    unlimited_yearly: 2,
    unlimited_monthly: 3,
    project_seat: 4,
    workspace_seat: 5,
};
const PROTO_TO_PLAN_SKU = {
    1: 'single_map',
    2: 'unlimited_yearly',
    3: 'unlimited_monthly',
    4: 'project_seat',
    5: 'workspace_seat',
};
const INTENT_TO_PROTO = {
    one_map: 1,
    more_than_one: 2,
};
const PROTO_TO_INTENT = {
    0: undefined,
    1: 'one_map',
    2: 'more_than_one',
};
const PROTO_TO_COUPON_REJECTION = {
    0: '',
    1: 'not_found',
    2: 'inactive',
    3: 'expired',
    4: 'already_used',
    5: 'limit_reached',
    6: 'not_applicable',
};
// An unrecognized code degrades to 'not_found' rather than '': '' means the
// coupon was ACCEPTED, and showing a refusal as an acceptance is the one
// mistake here that would misprice the checkout in the customer's view.
const couponRejectionFromProto = (v) => { var _a; return (_a = PROTO_TO_COUPON_REJECTION[v]) !== null && _a !== void 0 ? _a : 'not_found'; };
const PROTO_TO_ACCESS_LEVEL = {
    0: 'none',
    1: 'scouting',
    2: 'demo',
    3: 'full',
};
class BillingService {
    createAuthenticatedClient() {
        return grpcClientFactory.createAuthenticatedBillingClient({ enableCompression: true });
    }
    authMetadata() {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        return { authorization: `Bearer ${sessionId}|${sessionToken}` };
    }
    // Wraps one unary call so every method below is a three-liner instead of a
    // hand-rolled promise with its own subtly different error string.
    call(method, request, map, label) {
        return new Promise((resolve, reject) => {
            try {
                const client = this.createAuthenticatedClient();
                client[method](request, this.authMetadata(), (err, response) => {
                    if (err) {
                        reject(new Error(`${label}: ${err.code} ${err.message}`));
                        return;
                    }
                    if (!response) {
                        reject(new Error(`${label}: no response received`));
                        return;
                    }
                    resolve(map(response));
                });
            }
            catch (error) {
                reject(error);
            }
        });
    }
    // Admin-tuned prices for both SKUs plus the demo window length.
    async getPlanCatalog() {
        return this.call('getPlanCatalog', new proto.landscope.mapviewer.v1.GetPlanCatalogRequest(), (r) => ({
            demoWindowDays: r.getDemoWindowDays(),
            currencyCode: r.getCurrencyCode(),
            plans: (r.getPricesList() || []).map((p) => {
                var _a;
                return ({
                    sku: (_a = PROTO_TO_PLAN_SKU[p.getSku()]) !== null && _a !== void 0 ? _a : 'single_map',
                    demoPriceCents: p.getDemoPriceCents(),
                    fullPriceCents: p.getFullPriceCents(),
                    currencyCode: p.getCurrencyCode(),
                    isSubscription: p.getIsSubscription(),
                    displayName: p.getDisplayName(),
                });
            }),
        }), 'Failed to load plan catalog');
    }
    // What the caller can access, per project — resolved server-side through the
    // same rule OPA enforces, so this view can never disagree with the gate.
    async getMyEntitlements() {
        return this.call('getMyEntitlements', new proto.landscope.mapviewer.v1.GetMyEntitlementsRequest(), (r) => ({
            activePlans: (r.getActivePlansList() || []).map(accountPlanFromProto),
            projects: (r.getProjectsList() || []).map((p) => {
                var _a, _b;
                return ({
                    projectId: p.getProjectId(),
                    status: p.getProcessingStatus(),
                    accessLevel: (_a = PROTO_TO_ACCESS_LEVEL[p.getAccessLevel()]) !== null && _a !== void 0 ? _a : 'none',
                    demoExpiresAt: ((_b = p.hasDemoExpiresAt) === null || _b === void 0 ? void 0 : _b.call(p)) ? p.getDemoExpiresAt() : undefined,
                    customerIntent: PROTO_TO_INTENT[p.getCustomerIntent()],
                });
            }),
        }), 'Failed to load entitlements');
    }
    // Open a Stripe Checkout Session for a SKU and record the pending receipt.
    // projectId is required for single_map and ignored for the subscription.
    async initiateCheckout(sku, options = {}) {
        const request = new proto.landscope.mapviewer.v1.InitiatePlanCheckoutRequest();
        request.setSku(PLAN_SKU_TO_PROTO[sku]);
        if (options.projectId)
            request.setProjectId(options.projectId);
        if (options.couponCode)
            request.setCouponCode(options.couponCode);
        return this.call('initiateCheckout', request, (r) => {
            var _a;
            return ({
                checkoutSessionId: r.getCheckoutSessionId(),
                clientSecret: r.getClientSecret(),
                amountCents: r.getAmountCents(),
                grossAmountCents: r.getGrossAmountCents(),
                discountCents: r.getDiscountCents(),
                creditCents: r.getCreditCents(),
                currencyCode: r.getCurrencyCode(),
                sku: (_a = PROTO_TO_PLAN_SKU[r.getSku()]) !== null && _a !== void 0 ? _a : sku,
                couponCodeApplied: r.getCouponCodeApplied(),
                couponRejectionReason: couponRejectionFromProto(r.getCouponRejectionReason()),
                couponExpiresAt: r.hasCouponExpiresAt() ? r.getCouponExpiresAt() : undefined,
            });
        }, 'Failed to start checkout');
    }
    // Price a SKU without buying it. Pitch surfaces quote through this instead of
    // hardcoding a price, so admin-tuned pricing and any standing unlock credit
    // both reach the copy the customer reads.
    async quotePlan(sku, options = {}) {
        const request = new proto.landscope.mapviewer.v1.QuotePlanRequest();
        request.setSku(PLAN_SKU_TO_PROTO[sku]);
        if (options.projectId)
            request.setProjectId(options.projectId);
        if (options.couponCode)
            request.setCouponCode(options.couponCode);
        return this.call('quotePlan', request, (r) => {
            var _a;
            return ({
                sku: (_a = PROTO_TO_PLAN_SKU[r.getSku()]) !== null && _a !== void 0 ? _a : sku,
                grossAmountCents: r.getGrossAmountCents(),
                discountCents: r.getDiscountCents(),
                creditCents: r.getCreditCents(),
                amountCents: r.getAmountCents(),
                currencyCode: r.getCurrencyCode(),
                duringDemo: r.getDuringDemo(),
                couponValid: r.getCouponValid(),
                couponError: r.getCouponError(),
                couponRejectionReason: couponRejectionFromProto(r.getCouponRejectionReason()),
                couponExpiresAt: r.hasCouponExpiresAt() ? r.getCouponExpiresAt() : undefined,
            });
        }, 'Failed to price plan');
    }
    // Synchronously finalize a completed Checkout. The webhook is the backstop,
    // so this is idempotent and safe to race with it.
    async getCheckoutStatus(sessionId) {
        const request = new proto.landscope.mapviewer.v1.GetCheckoutStatusRequest();
        request.setSessionId(sessionId);
        return this.call('getCheckoutStatus', request, (r) => ({
            finalized: r.getFinalized(),
            status: r.getStatus(),
        }), 'Failed to get checkout status');
    }
    // Poll until the purchase is finalized or attempts run out. A false return is
    // NOT a failure — the webhook backstop will finalize shortly, so callers
    // should refresh state and keep a gentle "finalizing…" message.
    async finalizeCheckoutWithRetry(sessionId, attempts = 5, delayMs = 1200) {
        for (let i = 0; i < attempts; i++) {
            try {
                const { finalized, status } = await this.getCheckoutStatus(sessionId);
                if (finalized)
                    return true;
                if (status === 'unauthorized')
                    return false;
            }
            catch (_a) {
                // transient network/error — retry
            }
            await new Promise(r => setTimeout(r, delayMs));
        }
        return false;
    }
    // Record the "one map vs more than one" answer captured during the demo window.
    async setCustomerIntent(projectId, intent) {
        const request = new proto.landscope.mapviewer.v1.SetCustomerIntentRequest();
        request.setProjectId(projectId);
        request.setIntent(INTENT_TO_PROTO[intent]);
        return this.call('setCustomerIntent', request, (r) => r.getSuccess(), 'Failed to record intent');
    }
    // Validate an affiliate coupon against a SKU's server-owned price. The price
    // is never client-supplied, so a tampered request can't manufacture a discount.
    async validateCoupon(couponCode, sku, projectId) {
        const request = new proto.landscope.mapviewer.v1.ValidateCouponRequest();
        request.setCouponCode(couponCode);
        request.setSku(PLAN_SKU_TO_PROTO[sku]);
        if (projectId)
            request.setProjectId(projectId);
        return this.call('validateCoupon', request, (r) => {
            var _a, _b, _c;
            return ({
                valid: r.getValid(),
                errorMessage: r.getErrorMessage(),
                affiliateId: ((_a = r.hasAffiliateId) === null || _a === void 0 ? void 0 : _a.call(r)) ? r.getAffiliateId() : undefined,
                couponCodeId: ((_b = r.hasCouponCodeId) === null || _b === void 0 ? void 0 : _b.call(r)) ? r.getCouponCodeId() : undefined,
                discountPercent: ((_c = r.hasDiscountPercent) === null || _c === void 0 ? void 0 : _c.call(r)) ? r.getDiscountPercent() : undefined,
                grossAmountCents: r.getGrossAmountCents(),
                discountCents: r.getDiscountCents(),
                amountCents: r.getAmountCents(),
            });
        }, 'Failed to validate coupon');
    }
    // Stripe-hosted customer portal (receipts, payment method, subscription
    // management). Returns a one-time URL to redirect to.
    async createBillingPortalSession(returnUrl) {
        const request = new proto.landscope.mapviewer.v1.CreateBillingPortalSessionRequest();
        request.setReturnUrl(returnUrl);
        return this.call('createBillingPortalSession', request, (r) => r.getUrl(), 'Failed to open billing portal');
    }
    // Seat review: who the workspace's project collaborators are and how their
    // billing compares to the current plan's rules.
    async getSeatReviewState() {
        return this.call('getSeatReviewState', new proto.landscope.mapviewer.v1.GetSeatReviewStateRequest(), (r) => ({
            hasUnlimited: r.getHasUnlimited(),
            collaborators: (r.getCollaboratorsList() || []).map((c) => ({
                userId: c.getUserId(),
                email: c.getEmail(),
                firstName: c.getFirstName(),
                lastName: c.getLastName(),
                projectId: c.getProjectId(),
                projectName: c.getProjectName(),
            })),
            billableNow: r.getBillableNow(),
            quantityNow: r.getQuantityNow(),
            hasSubscription: r.getHasSubscription(),
            driftExists: r.getDriftExists(),
            projectSeatPriceCents: r.getProjectSeatPriceCents(),
            workspaceSeatPriceCents: r.getWorkspaceSeatPriceCents(),
            includedWorkspaceEditorSeats: r.getIncludedWorkspaceEditorSeats(),
            activeWorkspaceMembers: r.getActiveWorkspaceMembers(),
        }), 'Failed to get seat review state');
    }
    // Apply the current plan's project-seat math with the customer's consent.
    async confirmProjectSeatRebase() {
        return this.call('confirmProjectSeatRebase', new proto.landscope.mapviewer.v1.ConfirmProjectSeatRebaseRequest(), (r) => ({
            applied: r.getApplied(),
            needsCheckout: r.getNeedsCheckout(),
            billableNow: r.getBillableNow(),
            priceCents: r.getPriceCents(),
        }), 'Failed to confirm seat rebase');
    }
}
export const billingService = new BillingService();
